import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Loader2, Search } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAddJobToProject } from '@/hooks/useProjects';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

interface JobSearchResult {
  id: string;
  job_number: string;
  customer_name: string;
  status: string;
}

interface AddJobToProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  existingJobIds: string[];
}

export function AddJobToProjectDialog({
  open,
  onOpenChange,
  projectId,
  existingJobIds,
}: AddJobToProjectDialogProps) {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const addJob = useAddJobToProject();

  const { data: jobs, isLoading } = useQuery({
    queryKey: ['jobs-search', debouncedSearch],
    queryFn: async (): Promise<JobSearchResult[]> => {
      let q = supabase
        .from('jobs')
        .select('id, job_number, customer_name, status')
        .order('created_at', { ascending: false })
        .limit(20);

      if (debouncedSearch.trim()) {
        const safe = debouncedSearch.trim().replace(/[%_\\]/g, '\\$&');
        q = q.or(`job_number.ilike.%${safe}%,customer_name.ilike.%${safe}%`);
      }

      const { data, error } = await q;
      if (error) throw error;
      return data ?? [];
    },
    enabled: open,
    staleTime: 10 * 1000,
  });

  const handleAdd = (jobId: string) => {
    addJob.mutate(
      { projectId, jobId },
      { onSuccess: () => onOpenChange(false) }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Job to Project</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 pt-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Search job number or customer..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              autoFocus
            />
          </div>

          <div className="max-h-64 overflow-y-auto space-y-1">
            {isLoading ? (
              <div className="flex justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : jobs?.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">No jobs found</p>
            ) : (
              jobs?.map(job => {
                const alreadyAdded = existingJobIds.includes(job.id);
                return (
                  <div
                    key={job.id}
                    className="flex items-center justify-between rounded-lg border px-3 py-2.5"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-mono font-medium">{job.job_number}</p>
                      <p className="text-xs text-muted-foreground truncate">{job.customer_name}</p>
                    </div>
                    <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                      <Badge variant="outline" className="text-xs capitalize">
                        {job.status.replace(/_/g, ' ')}
                      </Badge>
                      <Button
                        size="sm"
                        variant={alreadyAdded ? 'secondary' : 'default'}
                        className="h-7"
                        disabled={alreadyAdded || addJob.isPending}
                        onClick={() => handleAdd(job.id)}
                      >
                        {alreadyAdded ? 'Added' : 'Add'}
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="flex justify-end pt-1">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Done</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
